import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { BROKER_TYPES, BROKER_TYPE_LABELS } from "@production-hub/shared";
import type { BrokerType } from "@production-hub/shared";
import type { CloudBroker } from "@/hooks/useBrokers";

const brokerSchema = z.object({
  name: z.string().min(1, "Name is required"),
  broker_type: z.enum(BROKER_TYPES),
  config: z.string().nullable().optional(),
  enabled: z.boolean().default(true),
  // Dynamic fields stored as JSON in config
  host: z.string().optional(),
  port: z.coerce.number().int().positive().optional(),
  username: z.string().optional(),
  password: z.string().optional(),
  region: z.string().optional(),
  apiKey: z.string().optional(),
});

type BrokerFormValues = z.infer<typeof brokerSchema>;

interface BrokerFormProps {
  broker?: CloudBroker;
  onSubmit: (data: {
    name: string;
    broker_type: BrokerType;
    config: string | null;
    enabled: boolean;
  }) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

export const BrokerForm = ({
  broker,
  onSubmit,
  onCancel,
  isLoading,
}: BrokerFormProps) => {
  const existingConfig =
    typeof broker?.config === "string"
      ? JSON.parse(broker.config)
      : broker?.config || {};

  const form = useForm<BrokerFormValues>({
    resolver: zodResolver(brokerSchema),
    defaultValues: {
      name: broker?.name ?? "",
      broker_type: (broker?.broker_type ?? "nimble") as BrokerType,
      enabled: broker?.enabled ?? true,
      host: existingConfig.host ?? "",
      port: existingConfig.port ?? undefined,
      username: existingConfig.username ?? "",
      password: existingConfig.password ?? "",
      region: existingConfig.region ?? "",
      apiKey: existingConfig.apiKey ?? "",
    },
  });

  const brokerType = form.watch("broker_type");

  const handleSubmit = (values: BrokerFormValues) => {
    const {
      name,
      broker_type,
      enabled,
      host,
      port,
      username,
      password,
      region,
      apiKey,
    } = values;

    let config: Record<string, unknown> = {};
    if (broker_type === "nimble") {
      config = { host, port: port || 8082, username, password };
    } else if (broker_type === "aws_mediaconnect") {
      config = { region };
    } else if (broker_type === "srt_gateway") {
      config = { host, port, apiKey };
    }

    onSubmit({
      name,
      broker_type,
      config: JSON.stringify(config),
      enabled,
    });
  };

  return (
    <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="name">Name</Label>
        <Input id="name" placeholder="Main Nimble" {...form.register("name")} />
        {form.formState.errors.name && (
          <p className="text-sm text-destructive">
            {form.formState.errors.name.message}
          </p>
        )}
      </div>

      <div className="space-y-2">
        <Label>Broker Type</Label>
        <Select
          value={brokerType}
          onValueChange={(v) => form.setValue("broker_type", v as BrokerType)}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {BROKER_TYPES.map((type) => (
              <SelectItem key={type} value={type}>
                {BROKER_TYPE_LABELS[type]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {(brokerType === "nimble" || brokerType === "srt_gateway") && (
        <>
          <div className="space-y-2">
            <Label htmlFor="host">Host</Label>
            <Input
              id="host"
              placeholder="192.168.1.50"
              {...form.register("host")}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="port">Port</Label>
            <Input
              id="port"
              type="number"
              placeholder={brokerType === "nimble" ? "8082" : ""}
              {...form.register("port")}
            />
          </div>
        </>
      )}

      {brokerType === "nimble" && (
        <>
          <div className="space-y-2">
            <Label htmlFor="username">Username</Label>
            <Input id="username" {...form.register("username")} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              {...form.register("password")}
            />
          </div>
        </>
      )}

      {brokerType === "aws_mediaconnect" && (
        <div className="space-y-2">
          <Label htmlFor="region">AWS Region</Label>
          <Input
            id="region"
            placeholder="eu-west-1"
            {...form.register("region")}
          />
        </div>
      )}

      {brokerType === "srt_gateway" && (
        <div className="space-y-2">
          <Label htmlFor="apiKey">API Key</Label>
          <Input id="apiKey" type="password" {...form.register("apiKey")} />
        </div>
      )}

      <div className="flex items-center space-x-2">
        <Switch
          id="enabled"
          checked={form.watch("enabled")}
          onCheckedChange={(checked) => form.setValue("enabled", checked)}
        />
        <Label htmlFor="enabled">Enabled</Label>
      </div>

      <div className="flex gap-2 pt-2">
        <Button type="submit" disabled={isLoading}>
          {broker ? "Update" : "Create"} Broker
        </Button>
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
};
